import * as React from 'react';
import { I18nContextProvider } from 'i18n/I18nProvider';
import Toast from 'components/Toast';
import toastNotification, { claimToastRendering } from 'state/toast';
import { ISessionData } from 'data/schema';
import GlobalConfirmDialog from 'components/ConfirmDialog/GlobalConfirmDialog';

export type TWebComponentProps = {
  [key: string]:
    | string
    | number
    | boolean
    | undefined
    | React.ReactNode
    | object
    | HTMLElement
    | ShadowRoot
    | ((...args: unknown[]) => unknown);
};

export interface IWebComponentContext extends TWebComponentProps {
  hasSlot?: boolean;
  rootElement?: ShadowRoot;
  webComponent?: HTMLElement & TWebComponentProps;
}

export interface IWebComponentRootProps extends Partial<ISessionData>, IWebComponentContext {
  children: React.ReactNode;
}

const WebComponentContext = React.createContext<IWebComponentContext | undefined>(undefined);

export function WebComponentRoot({ children, hasSlot, ...props }: IWebComponentRootProps) {
  const toastState = toastNotification.useState();
  const [shouldRenderToast, setShouldRenderToast] = React.useState(false);

  // Check if the web component is visible in the DOM
  const isWebComponentVisible = React.useCallback(() => {
    const webComponent = props.webComponent || props.rootElement?.host;
    if (!webComponent) return true; // If no reference, assume visible

    // Check if element is hidden via CSS
    const style = window.getComputedStyle(webComponent as Element);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
      return false;
    }

    return true;
  }, [props.webComponent, props.rootElement]);

  // Only decide to render toast once when toasts are added and not being rendered
  React.useEffect(() => {
    if (toastState.toasts.length > 0 && !toastState.isBeingRendered) {
      // Only claim toast rendering if this web component is actually visible
      if (isWebComponentVisible() && claimToastRendering()) {
        setShouldRenderToast(true);
      }
    } else if (toastState.toasts.length === 0) {
      setShouldRenderToast(false);
    }
  }, [toastState.toasts.length, toastState.isBeingRendered, isWebComponentVisible]);

  // Observe visibility changes when rendering toast to release claim if component becomes hidden
  React.useEffect(() => {
    if (!shouldRenderToast) return;

    const webComponent = props.webComponent || props.rootElement?.host;
    if (!webComponent) return;

    const checkVisibility = () => {
      if (!isWebComponentVisible()) {
        setShouldRenderToast(false);
        toastNotification.setValues({ isBeingRendered: false });
      }
    };

    // Observe style and attribute changes (display, visibility, opacity, etc.)
    const mutationObserver = new MutationObserver(checkVisibility);
    mutationObserver.observe(webComponent as Element, {
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    return () => {
      mutationObserver.disconnect();
    };
  }, [shouldRenderToast, isWebComponentVisible, props.webComponent, props.rootElement]);

  return (
    <WebComponentContext.Provider value={{ hasSlot, ...props }}>
      <I18nContextProvider>
        {shouldRenderToast && <Toast />}
        <GlobalConfirmDialog isHostVisible={isWebComponentVisible} />
        {children}
      </I18nContextProvider>
    </WebComponentContext.Provider>
  );
}

export default WebComponentRoot;

export const useWebComponent = (): IWebComponentContext => {
  const context = React.useContext(WebComponentContext);
  if (context === undefined) {
    // hook used without the WebComponentRoot parent
    return { hasSlot: false };
  }
  return context;
};
